import {
  EGameStatus,
  ESessionPhase,
  HandMainKey,
  IBoxEvent,
  ISessionBuyGameDetails,
  ISessionEvent,
  TBoxBodyDatabase,
  TCards,
} from '@lbs/shared-rp-types'
import { IOperator } from '../../api/operators'
import { IPlayer } from '../../api/players'
import prp2boxHttp from '../../services/prp2boxHttp'
import { IRpBonus } from '../bonuses'
import { IRpLimit } from '../limits'
import { IRpPayout } from '../payouts'
import { IRpStand } from '../stands'

export interface RpGame {
  id: number
  publicId: string
  name: string
  status: EGameStatus
  player: Pick<IPlayer, 'id' | 'login'>
  dealer: Pick<IOperator, 'id' | 'login'>
  limit: Pick<IRpLimit, 'id' | 'name'>
  stand: Pick<IRpStand, 'id' | 'name'>
  bonus: Pick<IRpBonus, 'id' | 'name'>
  payout: Pick<IRpPayout, 'id' | 'name' | 'data'>
  createdAt: string
  updatedAt: string
}

export interface IRpSession {
  id: number
  publicId: string
  gameId: number
  phase: ESessionPhase
  startedAt: string
  endedAt: string
  durationSec: number
  events: Array<ISessionEvent>
  boxes: Array<{
    seatNumber: number
    combination: HandMainKey
    cards: TCards[]
  }>
  dealer: {
    cards: TCards[]
    combination: HandMainKey
  }
  sessionNet: number
  buyGameDetails: ISessionBuyGameDetails | null
}

export interface IRpSessionWithNavigation extends IRpSession {
  navigation: {
    previousSessionId: number | null
    nextSessionId: number | null
    currentIndex: number
    totalSessions: number
  }
}

export interface IRpGameSessionBox {
  id: number
  gameId: number
  sessionId: number
  playerId: string
  seatNumber: number
  data: TBoxBodyDatabase
  events: IBoxEvent[]
}

export interface CreateRpGamePayload {
  name: string
  dealerId: string
  playerId: string
  limitId: number
  standId: number
  bonusId: number
  payoutId: number
}

export interface UpdateRpGamePayload extends CreateRpGamePayload {
  status: EGameStatus
}

class RpGamesApi {
  prefix: string = '/games'

  getAll = async (params: {
    limit?: number
    offset?: number
    dealerId?: string
  }): Promise<{
    rows: RpGame[]
    totalRows: number
  }> => {
    const response = await prp2boxHttp.get(this.prefix, { params })
    return response.data
  }

  getById = async (id: number): Promise<RpGame> => {
    const response = await prp2boxHttp.get(`${this.prefix}/${id}`)
    return response.data
  }

  getSessionsById = async (
    gameId: number,
    params?: {
      limit?: number
      offset?: number
      order?: 'ASC' | 'DESC'
    }
  ): Promise<{
    rows: IRpSession[]
    totalRows: number
  }> => {
    const response = await prp2boxHttp.get(`${this.prefix}/${gameId}/sessions`, { params })
    return response.data
  }

  getSessionByIds = async (gameId: number, sessionId: number): Promise<IRpSession> => {
    const response = await prp2boxHttp.get(`${this.prefix}/${gameId}/sessions/${sessionId}`)
    return response.data
  }

  getSessionNavigationByIds = async (gameId: number, sessionId: number): Promise<IRpSessionWithNavigation> => {
    const response = await prp2boxHttp.get(`${this.prefix}/${gameId}/sessions/${sessionId}/navigation`)
    return response.data
  }

  getBoxesByIds = async (
    gameId: number,
    sessionId: number
  ): Promise<{
    rows: IRpGameSessionBox[]
    totalRows: number
  }> => {
    const response = await prp2boxHttp.get(`${this.prefix}/${gameId}/sessions/${sessionId}/boxes`)
    return response.data
  }

  create = async (payload: CreateRpGamePayload): Promise<RpGame> => {
    const response = await prp2boxHttp.post(this.prefix, payload)
    return response.data
  }

  update = async (id: number, payload: UpdateRpGamePayload): Promise<RpGame> => {
    const response = await prp2boxHttp.put(`${this.prefix}/${id}`, payload)
    return response.data
  }

  finish = async (id: number): Promise<RpGame> => {
    const response = await prp2boxHttp.post(`${this.prefix}/${id}/finish`)
    return response.data
  }

  patch = async (id: number, payload: Partial<RpGame>): Promise<void> => {
    const response = await prp2boxHttp.patch(`${this.prefix}/${id}`, payload)
    return response.data
  }

  forceRestartSession = async (gameId: number) => {
    const response = await prp2boxHttp.post(`${this.prefix}/${gameId}/force-restart-session`)
    return response.data
  }
}

export default new RpGamesApi()
